import {
    Box,
    Button,
    Chip,
    Grid,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CopyIcon from '@mui/icons-material/FileCopy';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import HistoryIcon from '@mui/icons-material/History';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AssignmentReturnedIcon from '@mui/icons-material/AssignmentReturned';
import React, { MouseEvent, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { CustomLabel, PopoverLink } from '../../../../common';
import { fromLocalStorage, toLocalStorage } from '../../../../util/storage';
import { getAllBreadCumbLocations } from '../../../../router/RouterHelpers';
import { ContractInfo } from '../../../../store/api/ai-api';
import SvgIcon from '../../../../svg/SvgIcon';
import { ChangeContractComponent } from '../ChangeContractComponent';
import { ExecutionErrorDialog } from '../../ExecutionErrorDialog';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/reducers';
import { useData } from '@pure-ptr/react';
import { getClaimIds } from '../../../../store/api/claimAnalysis';
import { checkIfFHIRConfigExists } from '../../../../store/api/clients';
import { selectIsUniversalAdmin, selectHasCMSAccess } from '../../../../store/selectors/session';
import { infoToastify } from '../../../../util/toast';

const useStyles = makeStyles((theme) => ({
    titleDiv: (props: any) => ({
        padding: '16px 24px',
        borderBottom: `1px solid ${theme.palette.secondary.main}`,
        position: 'sticky',
        top: props?.sidebarOpen ? 0 : 45,
        zIndex: 96,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        '@media (max-width: 600px)': {
            padding: '10px 12px',
            top: 45,
        },
    }),
    chip: {
        marginLeft: '15px',
        fontWeight: 600,
        fontSize: '12px',
    },
    underline: {
        textDecoration: 'underline',
        color: '#2739c1',
        cursor: 'pointer',
    },
    prevNextButtons: {
        color: '#72777f',
        textTransform: 'none',
        '& .MuiButton-startIcon': { marginRight: '0px !important' },
        '& .MuiButton-endIcon': { marginLeft: '0px !important' },
    },
    moreChip: {
        color: '#94c83d',
        '& a': { color: '#94c83d', textDecoration: 'none' },
    },
}));

function ClaimViewHeader(props) {
    const {
        isFromModel, isFromTriage, isFromClaimAnalysis,
        claimAllDetails, hasJSONViewAccess, sections = [],
        getClaimDetailsPending, claimBulkOnDemandPending,
        claimId, comment, consolidatedClaims,
        openChat, closeChat,
        setTraceOpen, setHistoryOpen, setPatientDetailsOpen, setUploadAttachmentOpen,

        registerHeaderHandlers,
        reprocessClaimCallback,
        actions: {
            setAdjustment, setClaimServiceAdjustments, processBulkOnDemand,
            setClaimCommentsConfirmationDialog, getConsolidatedClaims,
            getModelConsolidatedClaims, resetConsolidatedClaimsView,
            resetModelConsolidatedClaimsView, setMulti, copyClaimToPreview
        },
        canAccessPayerMapping,
    } = props;

    const handleReprocessClaim = () => {
        const payload = {
            claimMetadata: [{
                claimNumber: claimAllDetails?.claimnumber,
                contractId: claimAllDetails?.contractid,
            }]
        };
        processBulkOnDemand(payload, props.onReprocessResult);
    };

    const classes = useStyles(props);
    const ContractsApi = new ContractInfo();
    const hasClaimChatBotAccess = useSelector<RootState>(state => state?.clients.hasClaimChatBotAccess);
    const isUniversalAdmin = useSelector(selectIsUniversalAdmin);
    const hasCMSAccess = useSelector(selectHasCMSAccess);
    // ── local state (was in BasicDetailsSection) ──────────────────────────
    const [isClaimnumberCopied, setCopied] = useState(false);
    const [isFHIRConfigured, setIsFHIRConfigured] = useState(false);

    const shouldUseClaimBackup = (function () {
        try {
            const histories = getAllBreadCumbLocations();
            if (histories?.length == 0) return false;
            const summaryDashboardIndex = histories.map(path => path?.includes('cms/summary-dashboard')).lastIndexOf(true);
            const claimAnalysisIndex = histories.map(path => path?.includes('/cms/claimAnalysis')).lastIndexOf(true);
            return claimAnalysisIndex !== -1 &&
                (summaryDashboardIndex === -1 || claimAnalysisIndex > summaryDashboardIndex);
        } catch (e) {
            console.error('Error processing history:', e);
            return false;
        }
    })();

    const [claimAnalysisBackup, setClaimAnalysisBackup] = useState((() => {
        if (isFromModel) return fromLocalStorage('modelAnalysis-backup');
        if (isFromTriage) return fromLocalStorage('triage-backup');
        if (isFromClaimAnalysis && shouldUseClaimBackup) return fromLocalStorage('claimAnalysis-backup');
        return [];
    })());


    const { isPreviousButtonAvailable, isNextButtonAvailable } = useMemo(() => {
        const currentIndex = claimAnalysisBackup?.findIndex(
            item => item === (isFromModel ? claimId : parseInt(claimId))
        );
        return {
            isPreviousButtonAvailable: currentIndex > 0,
            isNextButtonAvailable: currentIndex !== (claimAnalysisBackup?.length - 1) && currentIndex > -1,
        };
    }, [claimId, isFromModel, claimAnalysisBackup]);

    const [claimAnalysisFilterPage] = useState(fromLocalStorage('claimAnalysis-filter-page') ?? null);
    const [claimAnalysisPageSession, setClaimAnalysisPageSession] = useState(
        JSON.parse(sessionStorage.getItem('page-state:claimAnalysis-landing'))?.pagination ?? null
    );
    const [getNextPageClaimsIds, setGetNextPageClaimsIds] = useState(null);

    const { data: claimIds, isPending: isFetchingClaimIds } = useData(
        async abort => {
            if (!getNextPageClaimsIds) return;
            const options = {
                ...claimAnalysisFilterPage,
                page: { ...claimAnalysisFilterPage.pagination, currentPage: getNextPageClaimsIds.page }
            };
            delete options.significance;
            delete options.pagination;
            return await getClaimIds(claimAnalysisFilterPage?.significance || false, options);
        },
        [getNextPageClaimsIds]
    );

    const {} = useData(async () => {
        try {
            const response = await checkIfFHIRConfigExists();
            setIsFHIRConfigured(response);
            return response;
        } catch (error) {
            console.error(error);
        }
    }, []);

    // chat history for the history icon button
    const [claimAnalysisId, setClaimAnalysisId] = useState(null);
    useEffect(() => {
        if (claimAllDetails?.id) {
            setClaimAnalysisId(claimAllDetails?.id);
        }
        return () => { setClaimAnalysisId(null); };
    }, [claimAllDetails?.id]);
    const { data: chatHistoryData } = useData(async abort => {
        if (!claimAnalysisId) return;
        if (!hasClaimChatBotAccess) return;
        return await ContractsApi.getAllChatSessions(claimAnalysisId, 'claim');
    }, [claimAnalysisId, hasClaimChatBotAccess]);

    useEffect(() => {
        if (!claimAnalysisFilterPage) return;
        if (claimIds) {
            const newClaimIds = claimIds.results.map(item => item.id);
            if (!!getNextPageClaimsIds) {
                const existingIds = isFromModel
                    ? fromLocalStorage('modelAnalysis-backup')
                    : isFromTriage
                        ? fromLocalStorage('triage-backup')
                        : isFromClaimAnalysis && shouldUseClaimBackup
                            ? fromLocalStorage('claimAnalysis-backup')
                            : [];
                let updatedIds;
                if (getNextPageClaimsIds.mode === 'next') {
                    updatedIds = [...existingIds, ...newClaimIds];
                } else if (getNextPageClaimsIds.mode === 'previous') {
                    updatedIds = [...newClaimIds, ...existingIds];
                }
                const uniqueIds = [...new Set(updatedIds)];
                setClaimAnalysisBackup(uniqueIds);
                if (isFromModel) {
                    localStorage.setItem('modelAnalysis-backup', JSON.stringify(uniqueIds));
                } else if (isFromTriage) {
                    localStorage.setItem('triage-backup', JSON.stringify(uniqueIds));
                } else if (isFromClaimAnalysis && shouldUseClaimBackup) {
                    localStorage.setItem('claimAnalysis-backup', JSON.stringify(uniqueIds));
                }
                let finalData = uniqueIds.map(id => ({ id, page: getNextPageClaimsIds.page }));
                toLocalStorage('claimAnalysis-backup-page', [
                    ...fromLocalStorage('claimAnalysis-backup-page')?.filter(item => item.page !== getNextPageClaimsIds.page) || [],
                    ...finalData
                ]);
            } else {
                setClaimAnalysisBackup(newClaimIds);
            }
        }
    }, [claimIds, isFetchingClaimIds, claimAnalysisFilterPage]);

    useEffect(() => {
        if (claimAnalysisFilterPage) {
            if (!isPreviousButtonAvailable) {
                if (!!claimAnalysisPageSession && claimAnalysisPageSession?.currentPage > 1) {
                    setGetNextPageClaimsIds({
                        id: getNextPageClaimsIds?.id || 1 + 1,
                        page: claimAnalysisPageSession.currentPage - 1 === 0 ? 1 : claimAnalysisPageSession?.currentPage - 1,
                        mode: 'previous'
                    });
                }
            }
            if (!isNextButtonAvailable) {
                if (!!claimAnalysisPageSession && claimAnalysisPageSession?.currentPage < claimAnalysisPageSession?.totalPages) {
                    setGetNextPageClaimsIds({
                        id: getNextPageClaimsIds?.id || 1 + 1,
                        page: claimAnalysisPageSession?.currentPage + 1,
                        mode: 'next'
                    });
                }
            }
        }
        return () => {
            setGetNextPageClaimsIds(null);
            setClaimAnalysisPageSession(null);
        };
    }, [isPreviousButtonAvailable, isNextButtonAvailable]);

    useEffect(() => {
        let stateSessionStorage = JSON.parse(sessionStorage.getItem('page-state:claimAnalysis-landing')) ?? null;
        let listOfDetails = JSON.parse(localStorage.getItem('claimAnalysis-backup-page')) ?? [];
        const currentDetailId = claimAllDetails?.claimnumber || null;
        if (currentDetailId && listOfDetails.length > 0) {
            let currentDetail = listOfDetails.find(item => item.id === (isFromModel ? claimId : parseInt(claimId)));
            stateSessionStorage = {
                ...stateSessionStorage,
                pagination: {
                    ...stateSessionStorage?.pagination,
                    currentPage: currentDetail?.page || stateSessionStorage?.pagination?.currentPage,
                }
            };
            if (Object(stateSessionStorage).hasOwnProperty('filters')) {
                sessionStorage.setItem('page-state:claimAnalysis-landing', JSON.stringify(stateSessionStorage));
            }
            setClaimAnalysisPageSession(stateSessionStorage?.pagination ?? null);
        }
    }, [getNextPageClaimsIds, claimAllDetails]);

    // ── handlers (exact same logic as original BasicDetailsSection) ───────
    const handleNextClaim = (e: MouseEvent, confirm?: any) => {
        if (closeChat) closeChat();
        if (comment && !confirm) {
            setClaimCommentsConfirmationDialog({ open: true, state: 'next' });
            return;
        }
        const currentIndex = claimAnalysisBackup.findIndex(item => item === (isFromModel ? claimId : parseInt(claimId)));
        const nextClaimId = claimAnalysisBackup[currentIndex + 1];
        if (nextClaimId) {
            props.history.push(
                isFromModel ? `/cms/modeling/results/view/${nextClaimId}`
                    : isFromTriage ? `/cms/confidence-score-triage/view/${nextClaimId}`
                        : `/cms/claimAnalysis/view/${nextClaimId}`
            );
        }
    };

    const handlePreviousClaim = (e: MouseEvent, confirm?: any) => {
        if (closeChat) closeChat();
        if (comment && !confirm) {
            setClaimCommentsConfirmationDialog({ open: true, state: 'previous' });
            return;
        }
        const currentIndex = claimAnalysisBackup.findIndex(item => item === (isFromModel ? claimId : parseInt(claimId)));
        const prevClaimId = claimAnalysisBackup[currentIndex - 1];
        if (prevClaimId) {
            props.history.push(
                isFromModel ? `/cms/modeling/results/view/${prevClaimId}`
                    : isFromTriage ? `/cms/confidence-score-triage/view/${prevClaimId}`
                        : `/cms/claimAnalysis/view/${prevClaimId}`
            );
        }
    };

    const handleCopyClaimNumber = () => {
        const claimNumberStr = document.getElementById('claimnumber-chip').textContent;
        const claimNumber = claimNumberStr.substring(1);
        navigator.clipboard.writeText(claimNumber);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    };

    const handleOpenJsonView = () => {
        if (isFromModel) {
            localStorage.setItem('model_detail_Ids', claimId);
            props.history.push(`/cms/modeling/data-view/${claimAllDetails?.claimnumber}`);
        } else {
            props.history.push(
                isFromTriage
                    ? `/cms/confidence-score-triage/data-view/${claimAllDetails?.claimnumber}/${claimId}`
                    : `/cms/claimAnalysis/data-view/${claimAllDetails?.claimnumber}/${claimId}`
            );
        }
    };

    const handleClaimAudits = () => {
        const claimNumberStr = document.getElementById('claimnumber-chip').textContent;
        const claimNumber = claimNumberStr.substring(1);
        setMulti({ filter: [] });
        props.history.push(isFromTriage ? `/admin/claimAudits/${claimNumber}` : `/cms/claimAudits/${claimNumber}`);
    };

    const handleClaimAppeal = () => {
        const claimNumberStr = document.getElementById('claimnumber-chip').textContent;
        const claimNumber = claimNumberStr.substring(1);
        props.history.push({ pathname: `/cms/claimAnalysis/claimAppeal/${claimNumber}/${claimId}` });
    };

    const handleCopyClaimCallback = () => {
        infoToastify(
            <>
                <b>Copy Claim(s) to Preview Client</b>
                <br />
                Your request is submitted. You’ll be notified once it is copied.
            </>,
        );
    }

    const handleCopyClaim = () => {
        const claimNumberStr = document.getElementById('claimnumber-chip').textContent;
        const claimNumber = claimNumberStr.substring(1);
        const payload = {
            claimNumbers: [claimNumber]
        };
        
        copyClaimToPreview(payload, handleCopyClaimCallback)
    };

    const handleViewClaimAdjustment = () => {
        setAdjustment({ open: true, type: 'CLAIM', id: claimAllDetails?.paymentid });
    };

    const handleViewClaimServiceAdjustment = () => {
        setClaimServiceAdjustments({ open: true, id: claimAllDetails?.paymentid });
    };

    // Register next/prev handlers with ClaimViewPage so ConfirmationDialog
    // in BasicDetailsSection can call them via the ref pattern
    useEffect(() => {
        if (registerHeaderHandlers) {
            registerHeaderHandlers(handleNextClaim, handlePreviousClaim);
        }
    }, [handleNextClaim, handlePreviousClaim, registerHeaderHandlers]);

    // ── render: exact titleDiv JSX from original, zero changes ────────────
    return (
        <Grid container className={classes.titleDiv}>
            <Grid xs={12} sm={8} md={7} lg={8} item>
                <Grid container alignItems='center' sx={{ flexWrap: 'wrap' }}>
                    <Grid>
                        {isPreviousButtonAvailable &&
                            <Button onClick={handlePreviousClaim} className={classes.prevNextButtons} startIcon={<ArrowBackIcon />} disabled={getClaimDetailsPending || claimBulkOnDemandPending}>
                                Previous
                            </Button>
                        }
                        {isNextButtonAvailable &&
                            <Button onClick={handleNextClaim} className={classes.prevNextButtons} endIcon={<ArrowForwardIcon />} disabled={getClaimDetailsPending || claimBulkOnDemandPending}>
                                Next
                            </Button>
                        }
                    </Grid>
                    <Chip id='claimnumber-chip' className={classes.chip} label={`#${claimAllDetails?.claimnumber ?? ''}`} color="primary" variant="outlined" />
                    <IconButton
                        disabled={!claimAllDetails?.claimnumber}
                        onClick={handleCopyClaimNumber}
                        color={isClaimnumberCopied ? 'secondary' : 'primary'}
                        size="large">
                        <CopyIcon />
                        {isClaimnumberCopied && <span className='font-12 ml-2'>Copied</span>}
                    </IconButton>

                    {(!isFromModel && hasClaimChatBotAccess) &&
                        <Box display="flex">
                            <Tooltip title="SlicedIQ Insights">
                                <IconButton
                                    size="small"
                                    onClick={() => openChat({
                                        contractId: claimAllDetails?.id,
                                        sessionId: 0,
                                        directoryId: 'root',
                                        claimNumber: claimAllDetails?.claimnumber
                                    })}
                                    disabled={claimAllDetails?.hasExecutionError || !claimAllDetails?.id}
                                    style={{
                                        filter: claimAllDetails?.hasExecutionError || !claimAllDetails?.id ? 'grayscale(1)' : 'none',
                                    }}
                                >
                                    <SvgIcon className='chat-icon' id='chat-icon' width="19" height="19" />
                                </IconButton>
                            </Tooltip>
                            {chatHistoryData && chatHistoryData.length > 0 &&
                                <IconButton
                                    size="small"
                                    disabled={claimAllDetails?.hasExecutionError || !claimAllDetails?.id}
                                    onClick={() =>
                                        props.history.push(
                                            "/cms/claimAnalysis/ai-chats-history/" +
                                            claimAllDetails?.id + "?claimNumber=" + claimAllDetails?.claimnumber
                                        )
                                    }
                                >
                                    <Tooltip title="History">
                                        <HistoryIcon />
                                    </Tooltip>
                                </IconButton>
                            }
                        </Box>
                    }

                    {isFHIRConfigured &&
                        <IconButton size='small' title={'Patient Details'} onClick={() => setPatientDetailsOpen(true)}>
                            <MedicalInformationIcon />
                        </IconButton>
                    }

                    {hasCMSAccess && claimAllDetails?.isDenial &&
                        <IconButton size="small" title="Reconsideration/Appeal" onClick={handleClaimAppeal}>
                            <AssignmentReturnedIcon />
                        </IconButton>
                    }                    
                        <IconButton size='small' title={'Upload Claim Attachment'} onClick={() => setUploadAttachmentOpen(true)}>
                            <CloudUploadIcon />
                        </IconButton>                    

                    {consolidatedClaims && consolidatedClaims.length > 0 &&
                        consolidatedClaims?.map((cClaim, index) => {
                            if (index <= 3) {
                                return <Chip id={`cc-chip-${index}`} className={classes.chip} label={`#${cClaim ?? ''}`} color="secondary" variant="outlined" />;
                            } else if (index === 4) {
                                let remainingClaimsIds = consolidatedClaims?.slice(index, consolidatedClaims?.length);
                                let remainingClaims = (consolidatedClaims?.length - index);
                                return <Chip id={`cc-chip-${index}`} className={`${classes.chip} ${classes.moreChip}`} label={
                                    <PopoverLink
                                        columnData={`+${remainingClaims < 0 ? 0 : remainingClaims} more`}
                                        hoverData={[{
                                            label: "",
                                            data: <ul>{remainingClaimsIds.map((cc, i) => { return <li key={i}>{cc}</li> })}</ul>
                                        }]}
                                        title={`Consolidated Claims`}
                                    />
                                } color="secondary" variant="outlined" />;
                            }
                        })
                    }
                </Grid>

                {!isFromModel &&
                    <Grid container sx={{ flexWrap: 'wrap', mt: { xs: '6px', sm: 0 } }}>
                        <Typography onClick={handleViewClaimAdjustment} color='primary' className={`${classes.underline} mr-2`}>
                            Claim Adjustments
                        </Typography>
                        <Typography onClick={handleViewClaimServiceAdjustment} color='primary' className={`${classes.underline} mr-2`}>
                            Service Adjustments
                        </Typography>
                        {hasJSONViewAccess &&
                            <Typography onClick={handleOpenJsonView} color='primary' className={`${classes.underline} mr-2`}>
                                Data View
                            </Typography>
                        }
                        {sections && sections.length > 0 &&
                            <Typography onClick={() => setTraceOpen(true)} color='primary' className={`${classes.underline} mr-2`}>
                                Trace
                            </Typography>
                        }
                        <Typography onClick={() => setHistoryOpen(true)} color='primary' className={classes.underline}>
                            History
                        </Typography>
                        <Typography onClick={handleClaimAudits} color='primary' className={`${classes.underline} ml-2`}>
                            Claim Audit
                        </Typography>
                    </Grid>
                }
            </Grid>

            {!isFromModel &&
                <Grid xs={12} sm={4} md={5} lg={4} item>
                    <Grid>
                        <CustomLabel
                            label="Payer Name"
                            data={claimAllDetails?.usePlanIdMapping ? claimAllDetails?.planPayerContract?.payerName : claimAllDetails?.payer?.name}
                        />
                    </Grid>
                    <Grid>
                        <ChangeContractComponent
                            claimAnalysis={claimAllDetails}
                            isContractOverriden={claimAllDetails?.overridenContractid}
                            setShouldReloadGrid={false}
                            handleReprocessClaim={handleReprocessClaim}
                            reprocessCallback={props.onReprocessResult}
                        />
                        {claimAllDetails?.overridenCriteriaContractId &&
                            <Tooltip title="System Overridden">
                                <AutoModeIcon style={{ marginLeft: '5px', fontSize: '22px' }} />
                            </Tooltip>
                        }
                        {claimAllDetails?.hasExecutionError &&
                            <ExecutionErrorDialog executionError={claimAllDetails?.executionError} />
                        }
                    </Grid>
                </Grid>
            }

            {isFromModel &&
                <Grid xs={4} md={5} lg={4} style={{ textAlign: 'right' }}>
                    <Button className='dataViewButton' onClick={handleOpenJsonView} color='primary' variant='contained'>
                        Data View
                    </Button>
                </Grid>
            }
        </Grid>
    );
}

export default withRouter(ClaimViewHeader);